import React from "react";
import { Grid, TextField, Typography, Box, Paper, Divider, Checkbox, FormControlLabel, InputAdornment } from "@mui/material";
import FirmProfile from "../../../../config/firmProfile";

const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', mb: 1 }}>{title}</Typography>
        <Box sx={{ pl: 2 }}>{children}</Box>
    </Box>
);

const StepReviewSign = ({ data, onChange }) => {
  const handleAckChange = (field, value) => {
      onChange({
          ...data,
          acknowledgement: { ...data.acknowledgement, [field]: value }
      });
  };

  const handleFirmChange = (field, value) => {
      onChange({
          ...data,
          firmSignOff: { ...data.firmSignOff, [field]: value }
      });
  };

  const handleEngagementChange = (field, value) => {
      onChange({
          ...data,
          engagementDetails: { ...data.engagementDetails, [field]: value }
      });
  };

  const { client, businessStructure, services, totals, firmSignOff, acknowledgement, engagementDetails } = data;
  const isBusinessEngagement = businessStructure?.pdfTitle === 'Business Engagement';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Review &amp; Sign</Typography>
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}>
        <Typography variant="overline" display="block" gutterBottom>Engagement Summary Preview</Typography>
        
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <Section title="Client Details">
                    <Typography variant="body2"><strong>Name:</strong> {client?.clientName}</Typography>
                    <Typography variant="body2"><strong>Email:</strong> {client?.clientEmail}</Typography>
                    {client?.businessName && <Typography variant="body2"><strong>Business:</strong> {client.businessName}</Typography>}
                </Section>
            </Grid>
            <Grid item xs={12} md={6}>
                <Section title="Business Structure">
                    <Typography variant="body2">{businessStructure?.structure} {businessStructure?.businessStructureOther && `(${businessStructure.businessStructureOther})`}</Typography>
                </Section>
            </Grid>
        </Grid>

        <Section title="Selected Services">
            <ServicesSummary services={services} />
        </Section>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
                Subtotal (Excl. GST): {parseFloat(totals?.subtotal || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                GST (10%): {parseFloat(totals?.gst || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
            </Typography>
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                Total (Incl. GST): {parseFloat(totals?.grandTotal || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}
            </Typography>
        </Box>
      </Paper>

      {/* Business Engagement Extra Fields */}
      {isBusinessEngagement && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#f0f4ff', borderColor: '#1a237e' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
            Engagement Details
          </Typography>

          {/* Accounting Software Fee */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Accounting Software Fee</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!engagementDetails?.accountingSoftwareFee}
                    onChange={(e) => handleEngagementChange('accountingSoftwareFee', e.target.checked)}
                  />
                }
                label="Include Accounting Software Fee in engagement letter"
              />
              {engagementDetails?.accountingSoftwareFee && (
                <TextField
                  label="Monthly Fee"
                  type="number"
                  size="small"
                  sx={{ width: 160 }}
                  value={engagementDetails?.accountingSoftwareFeePrice ?? 30}
                  onChange={(e) => handleEngagementChange('accountingSoftwareFeePrice', e.target.value === '' ? '' : Number(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { min: 0 }
                  }}
                  helperText="Per month"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Period of Engagement */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Period of Engagement</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={engagementDetails?.startDate || ''}
                  onChange={(e) => handleEngagementChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Date the engagement begins"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  size="small"
                  value={engagementDetails?.endDate || ''}
                  onChange={(e) => handleEngagementChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank for 'till further notice'"
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
             <Paper variant="outlined" sx={{ p: 2 }}>
                 <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>Firm Sign-off</Typography>
                 <Box sx={{ mb: 2 }}>
                     <Typography variant="body2" color="text.secondary">Digitally signed by:</Typography>
                     <Typography variant="body1" sx={{ fontFamily: 'cursive', fontSize: '1.2rem', my: 1 }}>{FirmProfile.PRINCIPAL_SIGNER.name}</Typography>
                     <Typography variant="caption">{FirmProfile.PRINCIPAL_SIGNER.title}</Typography>
                     <Typography variant="caption" display="block">{FirmProfile.COMPANY_NAME}</Typography>
                 </Box>
                 <TextField 
                    label="Signed Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={firmSignOff?.signedDate || ""}
                    onChange={(e) => handleFirmChange('signedDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                 />
             </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
             <Paper variant="outlined" sx={{ p: 2 }}>
                 <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>Client Acknowledgement</Typography>
                 <Grid container spacing={2}>
                     <Grid item xs={12}>
                         <TextField 
                            label="On Behalf Of (Entity)"
                            fullWidth
                            size="small"
                            value={acknowledgement?.onBehalfOf || ""}
                            onChange={(e) => handleAckChange('onBehalfOf', e.target.value)}
                            placeholder="e.g. ABC Pty Ltd"
                         />
                     </Grid>
                     <Grid item xs={12}>
                         <TextField 
                            label="Authorized Person / Director Name"
                            fullWidth
                            size="small"
                            value={acknowledgement?.directorName || ""}
                            onChange={(e) => handleAckChange('directorName', e.target.value)}
                            placeholder="e.g. John Doe (Director)"
                         />
                     </Grid>
                     <Grid item xs={12}>
                         <TextField 
                            label="Signed Date"
                            type="date"
                            size="small"
                            fullWidth
                            value={acknowledgement?.clientSignedDate || ""}
                            onChange={(e) => handleAckChange('clientSignedDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                         />
                     </Grid>
                 </Grid>
                 {acknowledgement?.directorName && (
                     <Box sx={{ mt: 2, p: 1, bgcolor: '#f0f0f0', borderRadius: 1 }}>
                         <Typography variant="caption" display="block">Digital Signature Preview:</Typography>
                         <Typography variant="body1" sx={{ fontFamily: 'cursive', color: 'green' }}>
                            Digitally signed by {acknowledgement.directorName} on {acknowledgement.clientSignedDate || '...'}
                         </Typography>
                     </Box>
                 )}
             </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const ServicesSummary = ({ services }) => {
    const all = [];
    ['bookkeeping', 'taxation', 'structuring'].forEach(key => {
        if (services?.[key]?.selected) {
            services[key].selected.forEach(s => {
                let label = s.label;
                if (s.duration) label += ` (${s.duration})`;
                all.push({ label, price: s.price });
            });
        }
        if (services?.[key]?.others) {
            services[key].others.forEach(s => {
                if (s.text) {
                    let label = `Other: ${s.text}`;
                    if (s.duration) label += ` (${s.duration})`;
                    all.push({ label, price: s.price });
                }
            });
        }
        if (services?.[key]?.other?.text) {
             all.push({ label: `Other: ${services[key].other.text}`, price: services[key].other.price });
        }
    });

    const formatPrice = (price) => {
        if (price === "" || price === undefined || price === null) return "-";
        return parseFloat(price).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
    };

    if (all.length === 0) return <Typography variant="body2">No services selected.</Typography>;

    return (
        <Box>
            {all.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, borderBottom: '1px dashed #eee' }}>
                    <Typography variant="body2">{item.label}</Typography>
                    <Typography variant="body2">{formatPrice(item.price)}</Typography>
                </Box>
            ))}
        </Box>
    );
};

export default StepReviewSign;
